import React, {
  memo, useState, useRef, useCallback, useEffect
} from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Formats a duration in seconds as "m:ss".
 * @param {number} seconds - Duration in seconds.
 * @returns {string} Formatted string (e.g. "1:30").
 */
const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/** Seconds to skip when using rewind or forward controls. */
const SKIP_SECONDS = 15;

/**
 * Audio player for call recordings with progress bar, play/pause, and ±15s skip.
 * Uses HTML5 audio for cross-browser support. Expects i18n namespace "CallEvent" with
 * view_call_sidebar.* keys.
 *
 * @param {Object} props - Component props.
 * @param {string} [props.recordingUrl] - URL of the recording audio file. If empty, shows "unavailable" message.
 * @returns {React.ReactElement} Player UI or unavailable/error message.
 */
const CallRecordingPlayerMC = memo(({ recordingUrl }) => {
  const { t } = useTranslation('CallEvent');
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) setDuration(audioRef.current.duration);
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const handlePlay = useCallback(() => {
    if (!audioRef.current || !recordingUrl) return;
    audioRef.current.play().then(() => setIsPlaying(true)).catch(() => setHasError(true));
  }, [recordingUrl]);

  const handlePause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  const handleSeek = useCallback((newTime) => {
    if (!audioRef.current) return;
    const clamped = Math.max(0, Math.min(duration, newTime));
    audioRef.current.currentTime = clamped;
    setCurrentTime(clamped);
  }, [duration]);

  const handleRewind = useCallback(() => {
    handleSeek(currentTime - SKIP_SECONDS);
  }, [currentTime, handleSeek]);

  const handleForward = useCallback(() => {
    handleSeek(currentTime + SKIP_SECONDS);
  }, [currentTime, handleSeek]);

  const handleProgressClick = useCallback((e) => {
    if (!audioRef.current || duration <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    handleSeek(percent * duration);
  }, [duration, handleSeek]);

  useEffect(() => {
    if (!recordingUrl) return;
    setCurrentTime(0);
    setDuration(0);
    setHasError(false);
  }, [recordingUrl]);

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const remainingSeconds = Math.max(0, duration - currentTime);

  if (!recordingUrl) {
    return (
      <p className="tw-text-gray-500 tw-text-sm">
        {t('view_call_sidebar.recording_unavailable')}
      </p>
    );
  }

  return (
    <>
      <audio
        ref={audioRef}
        src={recordingUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={() => setHasError(true)}
        preload="metadata"
        crossOrigin="anonymous"
      />

      {hasError ? (
        <p className="tw-text-red-600 tw-text-sm">
          {t('view_call_sidebar.recording_error')}
        </p>
      ) : (
        <div className="tw-w-full tw-space-y-2">
          <div
            className="tw-relative tw-h-2 tw-bg-gray-200 tw-rounded-full tw-cursor-pointer tw-group"
            onClick={handleProgressClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleProgressClick(e); }}
            role="slider"
            tabIndex={0}
            aria-valuenow={currentTime}
            aria-valuemin={0}
            aria-valuemax={duration}
            aria-label={t('view_call_sidebar.progress_aria')}
          >
            <div
              className="tw-absolute tw-left-0 tw-top-0 tw-h-full tw-bg-gray-400 tw-rounded-full tw-transition-all"
              style={{ width: `${progressPercent}%` }}
            />
            <div
              className="tw-absolute tw-top-1/2 tw-w-3 tw-h-3 tw-bg-red-500 tw-rounded-full tw-border-2 tw-border-white tw-shadow -tw-translate-y-1/2 -tw-translate-x-1/2 tw-transition-all tw-pointer-events-none group-hover:tw-scale-110"
              style={{ left: `${progressPercent}%` }}
            />
          </div>

          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <span className="tw-text-gray-600 tw-text-sm tw-tabular-nums">
              {formatTime(currentTime)}
            </span>
            <div className="tw-flex tw-items-center tw-gap-1">
              <button
                type="button"
                onClick={handleRewind}
                disabled={duration <= 0}
                className="tw-p-1.5 tw-rounded-full hover:tw-bg-gray-100 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
                title={t('view_call_sidebar.rewind_15')}
                aria-label={t('view_call_sidebar.rewind_15')}
              >
                <svg width="19" height="21" viewBox="0 0 19 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.18457 20.2025C14.2207 20.2025 18.3691 16.054 18.3691 11.0179C18.3691 6.63215 15.2314 2.92317 11.0918 2.03547V0.708322C11.0918 -0.0035916 10.5381 -0.20574 9.94043 0.224924L6.84668 2.43977C6.36328 2.79133 6.37207 3.33625 6.84668 3.67903L9.93164 5.90266C10.5293 6.33332 11.0918 6.13996 11.0918 5.41926V4.15364C14.1064 4.97981 16.3037 7.73078 16.3037 11.0179C16.3037 14.9642 13.1309 18.1458 9.18457 18.1458C5.23828 18.1458 2.05664 14.9642 2.06543 11.0179C2.07422 8.79426 3.08496 6.85188 4.69336 5.54231C5.15039 5.11164 5.2998 4.51399 4.91309 4.03059C4.56152 3.58235 3.91992 3.52082 3.375 3.98664C1.34473 5.54231 0 8.27571 0 11.0179C0 16.054 4.14844 20.2025 9.18457 20.2025ZM6.74121 14.762C7.17188 14.762 7.44434 14.472 7.44434 14.015V8.64485C7.44434 8.05598 7.11914 7.74836 6.60938 7.74836C6.30176 7.74836 6.05566 7.84504 5.69531 8.13508L4.61426 8.94367C4.35938 9.1634 4.24512 9.35676 4.24512 9.61164C4.24512 9.95442 4.50879 10.2269 4.84277 10.2269C5.0625 10.2269 5.19434 10.1741 5.37012 9.98078L6.03809 9.40949H6.04688V14.015C6.04688 14.472 6.31934 14.762 6.74121 14.762ZM11.1094 14.8323C12.6035 14.8323 13.5791 13.8743 13.5791 12.389C13.5791 11.0618 12.6826 10.1917 11.5225 10.1917C10.9688 10.1917 10.4238 10.4554 10.1777 10.8773L10.3096 9.06672H12.7529C13.1045 9.06672 13.3594 8.81184 13.3594 8.4427C13.3594 8.07356 13.1045 7.83625 12.7529 7.83625H9.9668C9.46582 7.83625 9.17578 8.14387 9.14062 8.69758L8.97363 11.1849C8.93848 11.6771 9.21094 11.9495 9.65918 11.9495C10.0107 11.9495 10.1338 11.8704 10.3535 11.6947C10.6436 11.4486 10.8896 11.3167 11.2236 11.3167C11.8477 11.3167 12.2344 11.7738 12.2344 12.4329C12.2344 13.1009 11.7773 13.6019 11.1445 13.6019C10.6611 13.6019 10.292 13.3206 10.0635 12.9339C9.93164 12.7318 9.77344 12.6087 9.50977 12.6087C9.1582 12.6087 8.91211 12.8636 8.91211 13.2239C8.91211 13.3558 8.92969 13.4876 9 13.6282C9.2373 14.2171 10.0195 14.8323 11.1094 14.8323Z" fill="#23292E" />
                </svg>
              </button>
              <button
                type="button"
                onClick={isPlaying ? handlePause : handlePlay}
                disabled={!duration && !recordingUrl}
                className="tw-p-2.5 tw-rounded-full hover:tw-bg-gray-100 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors tw-text-gray-700"
                title={isPlaying ? t('view_call_sidebar.pause') : t('view_call_sidebar.play')}
                aria-label={isPlaying ? t('view_call_sidebar.pause') : t('view_call_sidebar.play')}
              >
                {isPlaying ? (
                  <svg width="21" height="23" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                  </svg>
                ) : (
                  <svg width="21" height="23" viewBox="0 0 21 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1.92969 22.7246C2.48828 22.7246 2.9834 22.5342 3.61816 22.166L18.6875 13.4316C19.8174 12.7842 20.3252 12.2383 20.3252 11.3623C20.3252 10.499 19.8174 9.95312 18.6875 9.29297L3.61816 0.558594C2.9834 0.19043 2.48828 0 1.92969 0C0.825195 0 0 0.850586 0 2.20898V20.5156C0 21.8867 0.825195 22.7246 1.92969 22.7246Z" fill="#23292E" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                onClick={handleForward}
                disabled={duration <= 0}
                className="tw-p-1.5 tw-rounded-full hover:tw-bg-gray-100 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
                title={t('view_call_sidebar.forward_15')}
                aria-label={t('view_call_sidebar.forward_15')}
              >
                <svg width="19" height="21" viewBox="0 0 19 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 10.9788C0 16.0062 4.13965 20.1458 9.16699 20.1458C14.1943 20.1458 18.334 16.0062 18.334 10.9788C18.334 8.21907 16.9717 5.49446 14.9238 3.92122C14.3877 3.48177 13.7637 3.5345 13.4297 3.98275C13.0518 4.45736 13.2012 5.03743 13.6494 5.45052C15.2842 6.76888 16.3125 8.72884 16.3213 10.9788C16.3301 14.9427 13.1309 18.1331 9.16699 18.1331C5.20312 18.1331 2.02148 14.9427 2.02148 10.9788C2.02148 7.66536 4.23633 4.9056 7.27734 4.097V5.34505C7.27734 6.04818 7.83105 6.25032 8.41113 5.82845L11.4785 3.63118C11.9531 3.2972 11.9531 2.76107 11.4785 2.41829L8.41992 0.221027C7.83105 -0.200848 7.27734 -0.00748831 7.27734 0.704426V2.014C3.14648 2.8929 0 6.60189 0 10.9788ZM6.70605 14.7054C7.12793 14.7054 7.40039 14.4242 7.40039 13.9671V8.58821C7.40039 8.01693 7.08398 7.70931 6.57422 7.70931C6.27539 7.70931 6.03809 7.80599 5.67773 8.08724L4.59668 8.89583C4.3418 9.11556 4.22754 9.30013 4.22754 9.55501C4.22754 9.889 4.49121 10.1702 4.81641 10.1702C5.03613 10.1702 5.16797 10.1087 5.33496 9.92415L6.01172 9.35286H6.02051V13.9671C6.02051 14.4242 6.29297 14.7054 6.70605 14.7054ZM11.083 14.7845C12.5771 14.7845 13.5439 13.8265 13.5439 12.3499C13.5439 11.0228 12.6562 10.1527 11.4873 10.1527C10.9336 10.1527 10.3887 10.4076 10.1426 10.8294L10.2744 9.01888H12.7266C13.0781 9.01888 13.333 8.764 13.333 8.40364C13.333 8.04329 13.0781 7.80599 12.7266 7.80599H9.94922C9.44824 7.80599 9.16699 8.11361 9.13184 8.64974L8.95605 11.137C8.9209 11.6292 9.19336 11.8929 9.63281 11.8929C9.98438 11.8929 10.1074 11.8226 10.3271 11.6468C10.626 11.3919 10.8721 11.2601 11.1973 11.2601C11.8301 11.2601 12.2256 11.7259 12.2256 12.3851C12.2256 13.0618 11.7598 13.5716 11.127 13.5716C10.6348 13.5716 10.2656 13.2904 10.0283 12.9036C9.89648 12.6927 9.73828 12.5697 9.4834 12.5697C9.13184 12.5697 8.88574 12.8158 8.88574 13.1673C8.88574 13.3079 8.91211 13.431 8.97363 13.5804C9.21094 14.1693 9.99316 14.7845 11.083 14.7845Z" fill="#23292E" />
                </svg>
              </button>
            </div>
            <span className="tw-text-gray-600 tw-text-sm tw-tabular-nums">
              -
              {formatTime(remainingSeconds)}
            </span>
          </div>
        </div>
      )}
    </>
  );
});

CallRecordingPlayerMC.displayName = 'CallRecordingPlayerMC';

export default CallRecordingPlayerMC;
